<template>
    <div class="pages">
      <div
        v-for="p in pages"
        :key="p.pageNumber"
        class="page-item"
        :class="{ active: p.pageNumber === currentPageNumber }"
        @click="$emit('goPage', p.pageNumber)"
        ref="itemsRef"
        :data-page="p.pageNumber"
      >
        <div class="thumb">
          <img v-if="p.thumbUrl" :src="p.thumbUrl" alt="thumb" />
          <div v-else class="thumb-skel"></div>
  
          <div v-if="!p.analyzed" class="thumb-mask">
            <button
              class="btn primary small"
              type="button"
              :disabled="p.analyzing"
              @click.stop="$emit('analyze', p.pageNumber)"
            >
              {{ p.analyzing ? '分析中…' : '開始分析' }}
            </button>
            <div class="mask-note">此頁面尚未分析</div>
          </div>
        </div>
  
        <div class="pi-meta">
          <div class="pi-title">第 {{ p.pageNumber }} 頁</div>
          <div class="pi-sub">
            <span v-if="p.analyzed" class="ok">● 已分析（{{ p.blocks.length }} 區）</span>
            <span v-else class="no">● 未分析</span>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref } from 'vue'
  import type { PageState } from '@/types/ainotebook'
  
  const props = defineProps<{
    pages: PageState[]
    currentPageNumber: number
  }>()
  
  const emit = defineEmits<{
    (e: 'goPage', n: number): void
    (e: 'analyze', n: number): void
    (e: 'needThumb', n: number): void
  }>()
  
  const itemsRef = ref<HTMLElement[] | null>(null)
  let io: IntersectionObserver | null = null
  
  onMounted(() => {
    io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (!en.isIntersecting) continue
        const el = en.target as HTMLElement
        const n = Number(el.dataset.page)
        if (Number.isFinite(n)) emit('needThumb', n)
      }
    }, { rootMargin: '200px' })
  
    // wait a tick-ish
    setTimeout(() => {
      const list = document.querySelectorAll<HTMLElement>('.page-item[data-page]')
      list.forEach(el => io?.observe(el))
    }, 0)
  })
  
  onBeforeUnmount(() => {
    io?.disconnect()
    io = null
  })
  </script>
  
  <style scoped lang="scss">
  .pages {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 0 12px 12px;
    display: grid;
    gap: 10px;
  
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.12) transparent;
    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 999px; }
  }
  
  .page-item {
    display: grid;
    grid-template-columns: 88px 1fr;
    gap: 10px;
    padding: 10px;
    border-radius: 14px;
    border: 1px solid rgba(255,255,255,0.06);
    background: rgba(255,255,255,0.03);
    cursor: pointer;
    transition: 0.15s ease;
  
    &:hover { border-color: rgba(255,255,255,0.12); }
    &.active {
      border-color: rgba(29,123,255,0.65);
      box-shadow: 0 0 0 4px rgba(29,123,255,0.10);
    }
  
    .thumb {
      position: relative;
      border-radius: 10px;
      overflow: hidden;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(0,0,0,0.25);
  
      img { width: 100%; height: 100%; display: block; object-fit: cover; }
      .thumb-skel { width: 100%; height: 90px; background: rgba(255,255,255,0.06); }
  
      .thumb-mask {
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
        gap: 8px;
        padding: 10px;
        background: rgba(0,0,0,0.45);
        text-align: center;
  
        .mask-note {
          font-size: 11px;
          color: rgba(232,238,252,0.75);
        }
      }
    }
  
    .pi-meta {
      .pi-title { font-weight: 800; }
      .pi-sub { margin-top: 6px; font-size: 12px; color: rgba(232,238,252,0.65); }
      .ok { color: rgba(112, 227, 170, 0.95); }
      .no { color: rgba(255, 210, 115, 0.95); }
    }
  }
  
  .btn {
    height: 34px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.05);
    color: #e8eefc;
    cursor: pointer;
    font-weight: 700;
  }
  .btn.primary {
    background: linear-gradient(180deg, rgba(29,123,255,1), rgba(29,123,255,0.85));
    border-color: rgba(29,123,255,0.55);
  }
  </style>
  